import { loginByPrivy } from "@/api/auth";
import { defineStore } from "pinia";
import {
  disconnect,
  isConnected,
  getUserInfo,
  hasMasterPassword,
  isNeedRestoreWallet,
  particleAuth,
  restoreWallet,
} from "@particle-network/auth-core";
import { walletEntryPlugin, EntryPosition } from "@particle-network/wallet";
import { useAppStore } from "./app";
import { useUserStore } from "./user";
import { fetchUnstakedNfts as useUnstakedNftsStore } from "./nfts/unstakedNftsStore";
import { useInventoryStore } from "./nfts/inventoryStore";
import { Avalanche, AvalancheTestnet } from "@particle-network/chains";
import { cookies, setCookie, removeCookie } from "@/plugins/useCookies";
import { signOut } from "firebase/auth";
import { firebaseAuth } from "@plugins/firebase";
import { ParticleBridge } from "@/utils/particleNetwork";

export const useAuthStore = defineStore("auth", () => {
  const appStore = useAppStore();
  const userStore = useUserStore();

  const connectDialog = ref(false);
  const loginError = ref("");
  const signUpVrumbleDialog = ref(false);
  const isConnectedPN = ref(false);
  // const accessToken = ref();
  const tokenExpiresAt = useLocalStorage(
    `NEXUS-${import.meta.env.MODE}-tokenExpiresAt`,
    null
  );

  const defaultChainId = ref(
    import.meta.env.VITE_AUTH_PROVIDER === "privy"
      ? (import.meta.env.VITE_PRIVY_CHAIN_ID === "43113" ? AvalancheTestnet.id : Avalanche.id)
      : import.meta.env.VITE_LOCAL || import.meta.env.MODE === "production"
      ? Avalanche.id
      : AvalancheTestnet.id
  );

  const isLoggedIn = computed(() => !!accessToken.value);

  const accessToken = computed({
    get: () => cookies.get(`NEXUS-${import.meta.env.MODE}-accessToken`) || null,
    set: (val) => {
      const cookieKey = `NEXUS-${import.meta.env.MODE}-accessToken`;
      if (!val) {
        removeCookie(cookieKey);
        return;
      }

      setCookie(cookieKey, val, {
        path: "/",
        maxAge: 60 * 60 * 24 * 30,
      });
    },
  });

  // const init = async () => {
  //   try {
  //     appStore.startLoadingScreen("init");

  //     console.log("isConnected", isConnected());
  //     isConnectedPN.value = isConnected();

  //     if (!isConnectedPN.value) {
  //       const res = await thirdpartyLogin();
  //       isConnectedPN.value = res;
  //       if (!res) {
  //         return;
  //       }

  //       userStore.setParticleUser(getUserInfo());

  //       if (res && hasMasterPassword() && isNeedRestoreWallet()) {
  //         const password = await getMainPassword();
  //         if (await restoreWallet(password)) {
  //           userStore.setParticleUser(getUserInfo());
  //         }
  //       }
  //     }

  //     userStore.setParticleUser(getUserInfo());

  //     const chainId = Number(
  //       await particleAuth.ethereum.request({ method: "eth_chainId" })
  //     );
  //     userStore.chainId = chainId;

  //     if (chainId !== defaultChainId.value) {
  //       await particleAuth.ethereum.switchChain(defaultChainId.value);
  //       userStore.chainId = Number(
  //         await particleAuth.ethereum.request({ method: "eth_chainId" })
  //       );
  //     }

  //     // walletEntryPlugin.setWalletCore({
  //     //   ethereum: particleAuth.ethereum,
  //     // });
  //     // walletEntryPlugin.walletEntryCreate();

  //     const { token } = await loginByParticle({
  //       userUuid: userStore.particleUserInfo.uuid,
  //       userToken: userStore.particleUserInfo.token,
  //     });

  //     await loginFirebaseByToken(token);

  //     await nextTick();

  //     await getSelfProfile();

  //     if (userStore.userInfo && userStore.userInfo.firstName) {
  //       return;
  //     } else {
  //       signUpVrumbleDialog.value = true;
  //     }
  //   } catch (error) {
  //     console.error("Error during initialization:", error);
  //   } finally {
  //     appStore.endLoadingScreen("init");
  //   }
  // };

  let authAttempt = 0;
  const authenticateAndInitialize = async () => {
    loginError.value = "";
    const attempt = ++authAttempt;
    const providerName = ParticleBridge.authProvider;
    const { uuid, token: particleToken } = userStore.particleUserInfo || {};
    const isCurrent = () => attempt === authAttempt && ParticleBridge.authProvider === providerName &&
      userStore.particleUserInfo?.uuid === uuid &&
      userStore.particleUserInfo?.token === particleToken;
    try {
      appStore.startLoadingScreen("auth-init");

      // Step 1: Check if user is already connected to Particle

      // isConnectedPN.value = ParticleBridge.connected;
      // isConnectedPN.value = isConnected();

      // if (!isConnectedPN.value) {
      //   console.log("User not connected to Particle Network");
      //   appStore.endLoadingScreen("auth-init");
      //   return { success: false, reason: "not_connected" };
      // }

      // Step 2: Get user info from Particle
      // console.log("Getting Particle user info...");
      // const particleUser = getUserInfo();
      // if (!particleUser) {
      //   throw new Error("Failed to get Particle user info");
      // }

      // userStore.setParticleUser(particleUser);

      // Step 3: Handle wallet restoration if needed
      // if (hasMasterPassword() && isNeedRestoreWallet()) {
      //   console.log("Restoring wallet with master password...");
      //   try {
      //     const password = await getMainPassword();
      //     const restored = await restoreWallet(password);
      //     if (restored) {
      //       // Update user info after wallet restoration
      //       userStore.setParticleUser(ParticleBridge.getUserInfo());
      //     }
      //   } catch (error) {
      //     console.warn("Wallet restoration failed:", error);
      //   }
      // }

      // Step 4: Handle chain configuration
      console.log("Configuring blockchain network...");
      // const currentChainId = Number(
      //   await particleAuth.ethereum.request({ method: "eth_chainId" })
      // );
      userStore.chainId = ParticleBridge.getChainId();

      // Switch to default chain if needed
      if (userStore.chainId !== defaultChainId.value) {
        console.log(
          `Switching from chain ${userStore.chainId} to ${defaultChainId.value}`
        );
        // await particleAuth.ethereum.switchChain(defaultChainId.value);

        // // Verify chain switch
        // const newChainId = Number(
        //   await particleAuth.ethereum.request({ method: "eth_chainId" })
        // );
        // userStore.chainId = newChainId;

        const switched = await ParticleBridge.switchChain(defaultChainId.value);
        if (!isCurrent()) return;
        const newChainId = ParticleBridge.getChainId();
        if (switched === false || Number(newChainId) !== defaultChainId.value) {
          throw new Error("Wallet did not switch to the required network");
        }
        userStore.chainId = newChainId;
        console.log(`Chain switched successfully to ${newChainId}`);
      }

      // Step 5: Authenticate with backend
      console.log("Authenticating with backend...");
      let result;
      if (providerName === "privy") {
        const accessToken = await ParticleBridge.getAccessToken();
        if (!isCurrent()) return;
        result = await loginByPrivy(accessToken);
      } else {
        result = await loginByParticle({userUuid: uuid, userToken: particleToken});
      }
      const { token } = result;

      if (!isCurrent()) return;
      if (!token) {
        throw new Error("Failed to get authentication token from backend");
      }

      // Step 6: Login to Firebase
      console.log("Logging in to Firebase...");
      const firebaseUser = await loginFirebaseByToken(token);
      // Logout/account replacement while the token was loading cancels this continuation.
      if (!firebaseUser || !isCurrent()) return;

      // Step 7: Get user profile
      console.log("Fetching user profile...");
      const { user_profile } = await getSelfProfile();
      if (!isCurrent()) return;
      userStore.setUserInfo(user_profile);

      try {
        const unstakedNftsStore = useUnstakedNftsStore();
        // Only rescan an address already linked in the server profile.
        // A newly created Privy wallet must not silently replace a legacy wallet.
        const hasInventoryWallet = Boolean(user_profile.wallet_address?.trim());
        await unstakedNftsStore.fetchUnstakedNfts(1, 100, hasInventoryWallet);
      } catch (nftError) {
        console.error(
          "Failed to force refresh unstaked NFTs after login:",
          nftError
        );
      }

      if (!isCurrent()) return;
      // Step 8: Check if user needs to complete profile
      const hasCompleteProfile = user_profile.firstName;

      if (!hasCompleteProfile) {
        console.log("User profile incomplete, showing signup dialog");
        signUpVrumbleDialog.value = true;
        appStore.endLoadingScreen("auth-init");
        return;
      }

      console.log("Authentication and initialization completed successfully");
    } catch (error) {
      if (!isCurrent()) return;
      loginError.value = providerName === "privy"
        ? "Privy connected, but the NEXUS account link is not ready. Please retry once migration is enabled."
        : "Your wallet connected, but NEXUS sign-in failed. Please try again.";
      console.error("Authentication/initialization failed");
      throw error;
    } finally {
      if (attempt === authAttempt) appStore.endLoadingScreen("auth-init");
    }
  };

  function openConnectDialog() {
    if (import.meta.env.DEV) console.trace("openConnectDialog");
    connectDialog.value = true;
  }

  function closeConnectDialog() {
    connectDialog.value = false;
  }

  const openCreateProfileDialog = () => {
    signUpVrumbleDialog.value = true;
  };

  const closeCreateProfileDialog = () => {
    signUpVrumbleDialog.value = false;
  };

  const logout = async () => {
    ++authAttempt;
    isConnectedPN.value = false;
    signUpVrumbleDialog.value = false;
    accessToken.value = null;
    tokenExpiresAt.value = null;
    appStore.endLoadingScreen("auth-init");
    try {
      const inventoryStore = useInventoryStore();
      const unstakedStore = useUnstakedNftsStore();

      const p1 = signOut(firebaseAuth);
      const p2 = ParticleBridge.handleLogout();

      await Promise.all([p1, p2]);
      removeCookie(`NEXUS-${import.meta.env.MODE}-accessToken`);
      inventoryStore.reset();
      unstakedStore.reset();

      console.log("Logged out");

      return true;
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const tokenAlmostExpired = () => {
    return Date.now() >= tokenExpiresAt.value;
  };

  // watch(
  //   accessToken,
  //   async (token) => {
  //     if (token) return;

  //     if (isConnectedPN.value) {
  //       console.log("Access token expired or not set, logging out");
  //       // await logout();
  //       await authenticateAndInitialize();
  //     }
  //   },
  //   { immediate: true }
  // );

  watch(
    isLoggedIn,
    async (value) => {
      if (value === true) return;

      if (isConnectedPN.value) {
        console.log("User is not logged in, re-authenticating");
        await authenticateAndInitialize();
      }
    },
    { immediate: true }
  );

  return {
    connectDialog,
    loginError,
    isLoggedIn,
    defaultChainId,
    isConnectedPN,
    accessToken,
    tokenExpiresAt,
    signUpVrumbleDialog,
    openConnectDialog,
    closeConnectDialog,
    // init,
    authenticateAndInitialize,
    logout,
    openCreateProfileDialog,
    closeCreateProfileDialog,
    tokenAlmostExpired,
  };
});
